import axios from 'axios';
import { defineStore, storeToRefs } from 'pinia';
import { Ref, ref, watch } from 'vue';

import authorization from '@rygen/rygen-ui/authorization';
import { Loadable } from '@rygen/rygen-ui/model';

import api from '@/api';
import { LoadboardSource } from '@/model';

import { useCurrentUserStore } from './CurrentUser';
import { useParentEntityStore } from './ParentEntity';

export interface PerformLoadOptions {
  bypassCache: boolean;
}

export interface LoadboardIntegrationStore {
  configuredLoadboardSources: Ref<Loadable<LoadboardSource[]>>;
  hasConfiguredSource: (loadboardSource: LoadboardSource) => boolean;
  loadConfiguredLoadboards: (opts?: PerformLoadOptions) => Promise<void>;
}

const PERFORM_LOAD_DEFAULT_OPTIONS: PerformLoadOptions = {
  bypassCache: false,
};

const _useLoadboardIntegrationStore = defineStore(
  'loadboardIntegration',
  (): LoadboardIntegrationStore => {
    const { owningClient } = useParentEntityStore();
    const { selectedDomainId } = useCurrentUserStore();

    const configuredLoadboardSources: Ref<Loadable<LoadboardSource[]>> = ref({
      loading: false,
    });

    const loadConfiguredLoadboards = async (
      opts: PerformLoadOptions = PERFORM_LOAD_DEFAULT_OPTIONS,
    ) => {
      const options = { ...PERFORM_LOAD_DEFAULT_OPTIONS, ...opts };

      const requiresLoad =
        configuredLoadboardSources.value.data === undefined &&
        !configuredLoadboardSources.value.loading;

      if (options.bypassCache || requiresLoad) {
        return performLoad();
      }
    };

    const performLoad = async () => {
      if (!authorization.hasAuthority('commercialIntegration', 'read')) {
        return;
      }

      try {
        configuredLoadboardSources.value.loading = true;
        configuredLoadboardSources.value.data =
          await api.client.getConfiguredLoadboards();
      } catch (e) {
        if (axios.isCancel(e)) {
          return;
        }

        configuredLoadboardSources.value.error = e;
        console.error('Error loading configured loadboard integrations', e);
      } finally {
        configuredLoadboardSources.value.loading = false;
      }
    };

    const hasConfiguredSource = (loadboardSource: LoadboardSource): boolean =>
      configuredLoadboardSources.value.data?.includes(loadboardSource) ?? false;

    watch([selectedDomainId, owningClient], () => {
      configuredLoadboardSources.value = { loading: false };
    });

    return {
      configuredLoadboardSources,
      hasConfiguredSource,
      loadConfiguredLoadboards,
    };
  },
);

export const useLoadboardIntegrationStore = (): LoadboardIntegrationStore => {
  const store = _useLoadboardIntegrationStore();
  const { configuredLoadboardSources } = storeToRefs(store);
  const { hasConfiguredSource, loadConfiguredLoadboards } = store;

  return {
    configuredLoadboardSources,
    hasConfiguredSource,
    loadConfiguredLoadboards,
  };
};
